import React, { useMemo, useEffect } from 'react';
import { Loader2, Minus } from 'lucide-react';
import UnitImage from '@components/game/objects/UnitImage';
import useMetadataStore from '@store/useMetadataStore';
import './UnitsPreview.css';

/**
 * Helper to capitalize a slug into a display name
 * Example: "guardian-test" -> "Guardian Test"
 */
const slugToName = (slug) => {
  if (!slug) return 'Unknown';
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Helper to group purchased units by template name
 * Returns { name: { count, template } }
 * FAIL LOUDLY: Throws if unit_template or name is missing (data corruption)
 */
const groupPurchasedUnits = (purchasedUnits) => {
  if (!purchasedUnits || purchasedUnits.length === 0) return {};

  return purchasedUnits.reduce((acc, pu) => {
    const template = pu.unit?.unit_template;
    if (!template) {
      throw new Error(`[UnitsPreview] Purchased unit missing unit_template: ${JSON.stringify(pu)}`);
    }
    if (!template.name) {
      throw new Error(`[UnitsPreview] unit_template missing name: ${JSON.stringify(template)}`);
    }
    const name = template.name;
    if (!acc[name]) {
      acc[name] = { count: 0, template };
    }
    acc[name].count += 1;
    return acc;
  }, {});
};

/**
 * UnitsPreview - Displays a list of units with icons and counts
 *
 * Prefix CSS: xc- (Xhelos Component)
 *
 * Supports multiple display modes:
 * 1. Empty state with custom message
 * 2. Loading state with spinner
 * 3. Purchased units (grouped by template)
 * 4. Template units (from loadout definition)
 * 5. Editable mode with remove buttons (for shop)
 *
 * visual_data comes from METADATA (single source of truth for all unit templates)
 *
 * @param {Array} units - Array of { unit_template_slug, quantity } from LoadoutTemplate
 * @param {Array} purchasedUnits - Array of purchased unit objects to group and display
 * @param {string} emptyMessage - Message to show when no units
 * @param {boolean} isLoading - Shows loading spinner
 * @param {string} loadingMessage - Message to show while loading
 * @param {string} className - Additional CSS class
 * @param {boolean} editable - Enables edit mode with remove buttons
 * @param {Function} onRemoveUnit - Callback when remove button clicked (receives template name)
 * @param {boolean} isRemoving - Shows loading state on remove buttons
 */
export default function UnitsPreview({
  units = null,
  purchasedUnits = null,
  emptyMessage = 'Sin unidades',
  isLoading = false,
  loadingMessage = 'Cargando...',
  className = '',
  // Editable mode props
  editable = false,
  onRemoveUnit,
  isRemoving = false,
}) {
  // Get metadata state (stable references)
  const unitTemplates = useMetadataStore((state) => state.metadata?.unit_templates);
  const metadataLoaded = useMetadataStore((state) => state.loaded);

  // FAIL LOUDLY: Validate metadata has unit_templates when loaded
  useEffect(() => {
    if (metadataLoaded && !unitTemplates) {
      // Use getState to avoid subscription issues
      useMetadataStore.getState()._handleCorruptedMetadata(
        'metadata.unit_templates is missing or invalid',
        { hasMetadata: !!useMetadataStore.getState().metadata }
      );
    }
  }, [metadataLoaded, unitTemplates]);

  // Build visual_data map with useMemo to avoid infinite loops
  const visualDataBySlug = useMemo(() => {
    if (!unitTemplates || unitTemplates.length === 0) {
      return {};
    }

    const map = {};
    unitTemplates.forEach((t) => {
      if (t.slug) {
        map[t.slug] = t.visual_data;
      }
    });
    return map;
  }, [unitTemplates]);

  // Group purchased units by template name
  const groupedPurchased = useMemo(() => {
    return groupPurchasedUnits(purchasedUnits);
  }, [purchasedUnits]);

  // Determine what to render
  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return (
        <div className="xc-units-preview__loading">
          <Loader2 size={16} className="spinner" />
          <span>{loadingMessage}</span>
        </div>
      );
    }

    // If we have purchased units, show them grouped
    if (purchasedUnits && purchasedUnits.length > 0) {
      return (
        <div className="xc-units-preview__list">
          {Object.entries(groupedPurchased).map(([name, { count, template }]) => (
            <div key={name} className="xc-units-preview__unit" title={name}>
              <span className="xc-units-preview__count">{count}x</span>
              {template && (
                <UnitImage unitTemplate={template} size="small" />
              )}
              {editable && (
                <button
                  type="button"
                  className="xc-units-preview__remove"
                  onClick={() => onRemoveUnit?.(name)}
                  disabled={isRemoving}
                  title={`Vender 1 ${name}`}
                >
                  <Minus size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      );
    }

    // If we have template units definition, show them
    if (units && units.length > 0) {
      return (
        <div className="xc-units-preview__list">
          {units.map((u, idx) => {
            const unitName = slugToName(u.unit_template_slug);
            // Look up visual_data from purchasedUnitsForVisualData by slug
            const visualData = visualDataBySlug[u.unit_template_slug];
            const pseudoTemplate = {
              name: unitName,
              slug: u.unit_template_slug,
              visual_data: visualData,
            };
            return (
              <div key={idx} className="xc-units-preview__unit" title={unitName}>
                <span className="xc-units-preview__count">{u.quantity}x</span>
                <UnitImage unitTemplate={pseudoTemplate} size="small" />
              </div>
            );
          })}
        </div>
      );
    }

    // Empty state
    return (
      <div className="xc-units-preview__empty">
        {emptyMessage}
      </div>
    );
  };

  return (
    <div className={`xc-units-preview ${className}`.trim()}>
      {renderContent()}
    </div>
  );
}
